import { h as __h } from "preact";
import { Component, h } from "preact";
/** @import { ComponentChildren, RefObject } from "preact"; */

import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
import { pluralize } from "~/util/pluralize.js";
/** @import { paginatedResponse, query } from "~/lib/api.js"; */

/** @import { DropdownItem } from "~/component/Dropdown.js"; */
import Data from "~/component/Data.js";

/** @import Pagination from "~/admin/component/Pagination.js"; */
/** @import { TableHeader } from "~/admin/component/Table.js"; */
import Table from "~/admin/component/Table.js";
import { deleteLocation } from "./actions/deleteLocation.js";
import { renameLocation } from "./actions/renameLocation.js";
import adminStyle from "~/admin/lib/style.js";
import util from "~/style/util.js";
/** @import { locationResponse } from "~/backend/validate.js"; */

/**
 * @extends {Component<LocationTableProps>}
 *
 * @typedef {object} LocationTableProps
 * @property {string} orgId
 * @property {ReturnType<
 * 	typeof query<
 * 		`/v2/orgs/${string}/locations`,
 * 		ReturnType<typeof paginatedResponse<locationResponse>>
 * 	>
 * >} query
 * @property {ComponentChildren} [footer]
 * @property {(h: TableHeader[]) => TableHeader[]} [headers]
 * @property {(
 * 	items: DropdownItem[],
 * 	location: locationResponse,
 * 	anchor: RefObject<HTMLButtonElement>,
 * ) => DropdownItem[]} [onTripleDots]
 * @property {InstanceType<typeof Pagination.State>} [paginate]
 * @property {InstanceType<typeof Table.Sort>} [sort]
 * @property {false} [scroll]
 */
export default class LocationTable extends Component {
  get #headers() {
    const h = [{
      id: "name",
      label: "Location",
      sort: "name"
    }, {
      id: "relays",
      label: "Relays"
    }, {
      id: "computers",
      label: "Computers"
    }];
    if (this.props.headers) return this.props.headers(h);
    return h;
  }

  /**
   * @returns {(
   * 	location: locationResponse,
   * 	anchor: { current: HTMLButtonElement },
   * ) => DropdownItem[]}
   */
  get #onTripleDots() {
    const canEdit = identity.permission(this.props.orgId, "locations", "edit");
    return (location, anchor) => {
      /** @type {DropdownItem[]} */
      const items = [];
      if (canEdit) {
        items.push({
          icon: "pencil",
          text: "Rename location",
          onSelect: () => renameLocation({
            orgId: this.props.orgId
          }, location)
        }, {
          icon: "trash",
          text: "Delete location",
          kind: "negative",
          disabled: location.relay_count > 0,
          tooltip: location.relay_count > 0 ? "This location still contains relays" : undefined,
          onSelect: () => deleteLocation({
            orgId: this.props.orgId
          }, location)
        });
      }
      if (this.props.onTripleDots) return this.props.onTripleDots(items, location, anchor);
      return items;
    };
  }
  render() {
    return h(Data, {
      loading: this.props.query.loading && !this.props.query.data,
      error: this.props.query.error,
      children: h(/** @type {typeof Table<locationResponse>} */Table, {
        label: "location",
        headers: this.#headers,
        empty: "No locations found.",
        footer: this.props.footer,
        rows: this.props.query.data?.data.map(location => {
          return {
            key: location.id,
            label: location.name,
            value: location,
            columns: {
              name: LocationTable.primary(location),
              relays: LocationTable.relays(location),
              computers: LocationTable.computers(location)
            }
          };
        }) ?? [],
        paginate: this.props.paginate?.props(this.props.query),
        sort: this.props.sort?.props(),
        href: location => `/org/${this.props.orgId}/relays/${location.id}`,
        onTripleDots: this.#onTripleDots,
        scroll: this.props.scroll
      })
    });
  }

  /** @param {locationResponse} location */
  static primary(location) {
    return __h("div", {
      "class": adminStyle.withDesc
    }, __h("span", {
      "class": util.truncate
    }, location.name), __h("span", {
      "class": util.truncate + " " + adminStyle.desc
    }, location.description));
  }

  /** @param {locationResponse} location */
  static relays(location) {
    return `${location.relay_count} ${pluralize(location.relay_count, "relay")}`;
  }

  /** @param {locationResponse} location */
  static computers(location) {
    return `${location.machine_count} ${pluralize(location.machine_count, "computer")}`;
  }
}